import { SearchIcon } from "../../shared/Icons";
import VerticalDotsIcon from "../../shared/Icons/VerticalDotsIcon";
import { ReactComponent as Clear } from "../../shared/assets/clearXIcon.svg";
import { themeAtom } from "../../shared/atoms";
import { AdvancedVesselSearchFilters } from "../models/advanced-vessel-search.models";
import { AdvancedVesselSearchPanel } from "./AdvancedVesselSearchPanel";
import { Checkbox } from "./SearchCheckbox";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type SearchInputProps = {
  searchQuery: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchClick: () => Promise<void>;
  handleClearClick: () => Promise<void>;
  isChecked: boolean;
  toggleShowSelectedOnly: () => void;
  showInactiveVessels: boolean;
  toggleShowInactiveVessels: () => Promise<void>;
  advancedFilters: AdvancedVesselSearchFilters;
  onApplyAdvancedFilters: (filters: AdvancedVesselSearchFilters) => void;
  hasAdvancedSearch: boolean;
};

export function SearchInput({
  searchQuery,
  handleInputChange,
  handleSearchClick,
  handleClearClick,
  isChecked,
  toggleShowSelectedOnly,
  showInactiveVessels,
  toggleShowInactiveVessels,
  advancedFilters,
  onApplyAdvancedFilters,
  hasAdvancedSearch,
}: SearchInputProps) {
  const theme = useAtomValue(themeAtom);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closePanel = () => {
    setIsPanelOpen(false);
    triggerRef.current?.focus();
  };
  const clearAdvancedFilters = () => {
    onApplyAdvancedFilters({});
    closePanel();
  };

  // Dismiss only this search panel, and remove document listeners when it closes.
  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }

    const handleOutsidePointer = (event: PointerEvent) => {
      if (!searchContainerRef.current?.contains(event.target as Node)) {
        setIsPanelOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPanelOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", handleOutsidePointer);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPanelOpen]);

  return (
    <div
      data-test="search-vessels-toolbar"
      className={twMerge("flex flex-wrap items-center justify-start gap-4 p-4", theme.home.tailwind.panelBackground)}
    >
      <div className="relative" ref={searchContainerRef}>
        <div className="relative flex">
          <input
            type="text"
            className={twMerge(
              "h-12 w-[130px] max-w-[calc(100vw-12rem)] rounded-s border border-r-0 py-3.5 pl-4 pr-9 focus:border-green focus:outline-none sm:w-[180px]",
              theme.uwhcAssistant.inputBg,
              theme.uwhcAssistant.inputText,
              theme.uwhcAssistant.inputBorder,
            )}
            placeholder="Search"
            value={searchQuery}
            onChange={handleInputChange}
            aria-label="Search vessels"
            data-test="search-vessels-input"
          />
          {searchQuery && (
            <button
              aria-label="Clear vessel search"
              data-test="search-vessels-clear"
              className="absolute right-40 top-1/2 -translate-y-1/2 cursor-pointer"
              onClick={handleClearClick}
            >
              <Clear className="size-5 text-gray-400" />
            </button>
          )}
          <div className="flex size-12 shrink-0 items-center justify-center border-y border-gray-300 bg-white">
            {hasAdvancedSearch && (
              <button
                type="button"
                aria-label="Clear vessel filters and sorting"
                title="Clear filters and sorting"
                data-test="advanced-vessel-search-clear"
                onClick={clearAdvancedFilters}
                className="flex size-full items-center justify-center text-gray-400 hover:text-gray-700 focus-visible:outline-green"
              >
                <Clear className="size-5 translate-x-3" aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            ref={triggerRef}
            onClick={() => setIsPanelOpen((open) => !open)}
            aria-label="Advanced vessel search"
            aria-haspopup="dialog"
            aria-expanded={isPanelOpen}
            aria-controls="advanced-vessel-search-panel"
            data-test="advanced-vessel-search-trigger"
            className={twMerge(
              "relative flex size-12 shrink-0 items-center justify-center border-y border-gray-300 bg-white text-gray-700 focus-visible:outline-green",
              hasAdvancedSearch && "text-green",
            )}
          >
            <VerticalDotsIcon className="h-5 w-5 rotate-90" aria-hidden="true" />
          </button>
          <button
            className={twMerge("flex size-12 items-center justify-center rounded-e bg-green focus:outline-none")}
            onClick={handleSearchClick}
            aria-label="Search vessels"
            data-test="search-vessels-submit"
          >
            <SearchIcon className="size-6" color={theme.sideBarComponent.iconAndText.tooltipText} />
          </button>
        </div>
        {isPanelOpen && (
          <AdvancedVesselSearchPanel filters={advancedFilters} onApply={onApplyAdvancedFilters} onClose={closePanel} />
        )}
      </div>
      <div>
        <Checkbox
          label="Show only vessels not assigned to me"
          isChecked={isChecked}
          onChange={toggleShowSelectedOnly}
        />
      </div>
      <div>
        <Checkbox label="Show inactive vessels" isChecked={showInactiveVessels} onChange={toggleShowInactiveVessels} />
      </div>
    </div>
  );
}
